import React from 'react'
import { Switch, Route, Redirect, useHistory } from 'react-router-dom'
import { Icon } from 'antd-mobile'
import { useQuery } from 'react-query'
import { getSign } from '@feedback/services/index'
import { handleUrlParams } from '@feedback/utils/request'
import { getAppConfig } from '@feedback/utils/CONST'
import { isVestAppid } from '@feedback/utils/siteConfig'
import { omit } from 'lodash'
import qs from 'qs'
import QuestionIndex from '../pages/allfb/questionIndex/App'
import QuestionDetail from '../pages/allfb/questionDetail/App'
import QuestionList from '../pages/allfb/questionList/App'
import TicketList from '../pages/allfb/ticketList/App'
import TicketForm from '../pages/allfb/ticketForm/App'
import TicketDetail from '../pages/allfb/ticketDetail/App'
import TicketType from '../pages/allfb/ticketType/App'
import QuestionIndexBeta from '../pages/allfb/questionIndex'
import QuestionDetailBeta from '../pages/allfb/questionDetail'
import TicketFormBeta from '../pages/allfb/ticketForm'
import TicketListBeta from '../pages/allfb/ticketList'
import TicketDetailBeta from '../pages/allfb/ticketDetail'
import SearchResult from '../pages/allfb/searchResult'
import NetworkCheck from '../pages/allfb/networkCheck'
import ErrorPage from '../pages/allfb/error'
import VestHome from '../pages/allfb/vest/home'
import VestQuestionList from '../pages/allfb/vest/questionList'
import toast from 'react-hot-toast'

/**
 * `/` 按 appid 条件渲染：马甲站走类别网格首页，既有 app 保持原首页。
 * 门禁只看 isVestAppid，对 cm1009/cm1017 等零影响。
 */
const HomeEntry: React.FC<any> = (props) => {
  const { appid } = getAppConfig() || {}
  if (isVestAppid(appid)) {
    return <VestHome {...props} />
  }
  return <QuestionIndexBeta {...props} />
}

/**
 * `/questionList` 与 HomeEntry 同纪律：仅 vest appid 进马甲分类列表，
 * 非 vest 手输 URL 回落既有首页，避免无主题 vest 页外露。
 */
const QuestionListEntry: React.FC<any> = (props) => {
  const { appid } = getAppConfig() || {}
  if (isVestAppid(appid)) {
    return <VestQuestionList {...props} />
  }
  return <QuestionIndexBeta {...props} />
}

const routerMap = [
  { path: '/', component: HomeEntry, auth: true },
  { path: '/v1/questionIndex', component: QuestionIndex, auth: true },
  { path: '/v1/questionDetail', component: QuestionDetail, auth: true },
  { path: '/v1/questionList', component: QuestionList, auth: true },
  { path: '/v1/ticketList', exact: false, component: TicketList, auth: true },
  { path: '/v1/ticketForm', component: TicketForm, auth: true },
  { path: '/v1/ticketType', component: TicketType, auth: true },
  { path: '/v1/ticketDetail', component: TicketDetail, auth: true },
  { path: '/questionIndex', component: HomeEntry, auth: true },
  { path: '/questionDetail', component: QuestionDetailBeta, auth: true },
  { path: '/questionList', component: QuestionListEntry, auth: true },
  { path: '/ticketList', component: TicketListBeta, auth: true },
  { path: '/ticketForm', component: TicketFormBeta, auth: true },
  { path: '/ticketDetail', component: TicketDetailBeta, auth: true },
  { path: '/searchResult', component: SearchResult, auth: true },
  { path: '/networkCheck', component: NetworkCheck, auth: false },
  { path: '/error', component: ErrorPage, auth: false }
]

const getUrlRestStr = (url: string) => {
  const query = qs?.parse?.(url)
  const omitKeys = [
    'appid',
    'uuid',
    'did',
    'deviceName',
    'devicePlatform',
    'version',
    'package-name',
    'channel',
    'language',
    'from',
    'caseType',
    'network',
    'contextTitle',
    // 马甲站站点覆盖参数：身份已写入 sessionStorage，redirect 后不必再挂在 URL
    'site',
    '_app_id',
    '_app_lang',
    '_app_channel_id',
    '_app_version',
    '_app_game_version',
    '_app_res_version',
    '_app_install_id',
    '_app_activate_id',
    '_app_user_id',
    '_device_id',
    '_device_brand',
    '_device_model',
    '_os_type',
    '_os_version',
    '_ad_id',
    '_androidid',
    '_idfv'
  ]
  const rest = omit<any>(query, omitKeys)
  const queryStr = Object.keys?.(rest)
    .reduce((arr: string[], k: string) => {
      return [...arr, `${k}=${encodeURIComponent(rest?.[k])}`]
    }, [])
    .join('&')
  return queryStr
}

const Loading = ({ redirectPathname }: { redirectPathname?: string }) => {
  const history = useHistory()
  const params = handleUrlParams()
  const queryStr = getUrlRestStr(location?.hash?.split?.('?')?.[1])

  const sign = useQuery('sign', () => getSign(params), {
    onSuccess: (data) => {
      if (data?.data?.data) {
        sessionStorage.setItem('sign', data?.data?.data)
      }
      history.replace({
        pathname: redirectPathname || '/',
        search: queryStr
      })
    }
  })

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)'
      }}
    >
      <Icon type='loading' />
    </div>
  )
}

const feedbackRouter = (): JSX.Element => {
  return (
    <Switch>
      {routerMap.map((item, index) => {
        return (
          <Route
            key={index}
            path={item?.path}
            exact
            render={(props) => {
              const searchParams = Object.fromEntries(
                new URLSearchParams(props.location?.search?.split?.('?')?.[1])
              )
              const isRefreshSign =
                !sessionStorage?.getItem?.('sign') || searchParams?.appid

              try {
                sessionStorage?.getItem?.('headerFromApp')
              } catch (e) {
                toast(
                  `A cookie is a type of data stored on your device by a website to remember you when you visit again. Turning off this feature is safe as it allows websites to remember your preferences. For a smoother and more personalized browsing experience, you can follow these steps to adjust your settings:\n1. Go to Settings > Safari.\n2. Choose to Turn off the "Block All Cookies" option.`,
                  { duration: 6000 }
                )
              }

              if (item?.auth && isRefreshSign) {
                return <Loading redirectPathname={props?.location?.pathname} />
              }
              return React.createElement(item?.component, props)
            }}
          />
        )
      })}
      <Route
        render={({ location }) => {
          const { appid } = getAppConfig() || {}
          return isVestAppid(appid) ? (
            <Redirect to={{ pathname: '/', search: location.search }} />
          ) : null
        }}
      />
    </Switch>
  )
}

export default feedbackRouter
